import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Camera, X, RefreshCw } from "lucide-react";

/**
 * Live QR scanner using the device rear camera and the pure-JS jsQR decoder.
 * Emits the detected string exactly once via `onDecode(text)` and stops the
 * camera afterwards — the parent decides whether to close the modal.
 *
 * Gracefully handles: no camera permission, no camera at all (desktop),
 * and unmount during scanning (releases the MediaStream tracks so the LED
 * turns off immediately).
 */
export default function QrScanner({ onDecode, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const decodedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        v.setAttribute("playsinline", "true");
        await v.play();
        setReady(true);
        tick();
      } catch (e) {
        setError(e?.message || "Impossibile accedere alla fotocamera");
      }
    };

    const tick = () => {
      if (decodedRef.current) return;
      const v = videoRef.current, cv = canvasRef.current;
      if (!v || !cv || v.readyState !== v.HAVE_ENOUGH_DATA) {
        rafRef.current = requestAnimationFrame(tick); return;
      }
      const w = v.videoWidth, h = v.videoHeight;
      cv.width = w; cv.height = h;
      const ctx = cv.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(v, 0, 0, w, h);
      let data;
      try {
        data = ctx.getImageData(0, 0, w, h);
      } catch { rafRef.current = requestAnimationFrame(tick); return; }
      const code = jsQR(data.data, w, h, { inversionAttempts: "attemptBoth" });
      if (code && code.data && !decodedRef.current) {
        decodedRef.current = true;
        // Stop stream immediately so the camera LED goes off.
        (streamRef.current?.getTracks() || []).forEach((t) => t.stop());
        onDecode(code.data.trim());
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    start();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      (streamRef.current?.getTracks() || []).forEach((t) => t.stop());
    };
  }, [onDecode]);

  return (
    <div className="relative bg-black rounded overflow-hidden aspect-square" data-testid="qr-scanner">
      <video ref={videoRef} className="w-full h-full object-cover" muted playsInline/>
      <canvas ref={canvasRef} className="hidden"/>
      {/* Viewfinder overlay */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="w-2/3 aspect-square border-2 border-white/80 rounded-lg" style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.4)" }}/>
      </div>
      {!ready && !error && (
        <div className="absolute inset-0 flex items-center justify-center text-white text-sm gap-2" data-testid="qr-scanner-loading">
          <RefreshCw className="w-4 h-4 animate-spin"/> Accensione fotocamera…
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-sm px-6 text-center" data-testid="qr-scanner-error">
          <Camera className="w-8 h-8 mb-3 opacity-70"/>
          <div className="opacity-90 mb-2">{error}</div>
          <div className="text-xs opacity-70">Concedi il permesso fotocamera oppure inserisci il codice manualmente.</div>
        </div>
      )}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-2 right-2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center"
          aria-label="Chiudi scanner"
          data-testid="qr-scanner-close"
        >
          <X className="w-4 h-4"/>
        </button>
      )}
    </div>
  );
}
